/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useRef, useState, DragEvent, ChangeEvent } from'react';
import { cn } from'@/shared/ui/utils/tailwind';

export const IMAGE_MIME_TYPE = ['image/png','image/jpeg','image/jpg','image/gif','image/webp'];

export type DropzoneProps = {
  onDrop: (files: File[]) => void;
  onReject?: (files: File[]) => void;
  accept?: string[];
  maxSize?: number;
  multiple?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
};

/**
 * Dropzone component with Tailwind styling
 * Replaces @mantine/dropzone
 */
type DropzoneStateFC = React.FC<{ children: React.ReactNode }>;
type DropzoneComponent = React.FC<DropzoneProps> & {
  Idle: DropzoneStateFC;
  Accept: DropzoneStateFC;
  Reject: DropzoneStateFC;
};

export const Dropzone: DropzoneComponent = (({
  onDrop,
  onReject,
  accept = IMAGE_MIME_TYPE,
  maxSize,
  multiple = false,
  disabled = false,
  children,
  className,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    const typeOk = !accept || accept.includes(file.type);
    const sizeOk = !maxSize || file.size <= maxSize * 1024;
    return typeOk && sizeOk;
  };

  const partitionFiles = (fileList: File[]): { valid: File[]; rejected: File[] } => {
    const valid: File[] = [];
    const rejected: File[] = [];
    fileList.forEach((file) => (validateFile(file) ? valid : rejected).push(file));
    return { valid, rejected };
  };

  const dispatchFileResults = (valid: File[], rejected: File[]) => {
    if (valid.length > 0) onDrop(multiple ? valid : [valid[0]]);
    if (rejected.length > 0) onReject?.(rejected);
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const { valid, rejected } = partitionFiles(Array.from(files));
    dispatchFileResults(valid, rejected);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!disabled) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value ='';
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'border-2 border-dashed rounded transition-colors cursor-pointer',
        isDragging
          ?'border-[var(--seed-base)] bg-[var(--selection-active)]'
          :'border-[var(--border-medium)] hover:border-[var(--border-strong)]',
        disabled &&'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept?.join(',')}
        multiple={multiple}
        disabled={disabled}
        onChange={handleFileInput}
        className="hidden"
      />
      {children}
    </div>
  );
}) as DropzoneComponent;

// Dropzone state components for compatibility with Mantine API
Dropzone.Idle = ({ children }: { children: React.ReactNode }) => <>{children}</>;
Dropzone.Accept = ({ children }: { children: React.ReactNode }) => <>{children}</>;
Dropzone.Reject = ({ children }: { children: React.ReactNode }) => <>{children}</>;

export default Dropzone;
